import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  getAllPresupuestoA3Pedidos,
  getA3PedidoLineasMatriz,
  getPresupuestoA3Pedidos,
  searchA3Articulos,
} from "../../api/Produccion/a3ArticulosApi";
import EnvioMatrizLectura, {
  a3MatrizRowsToLineasEnvio,
} from "./EnvioMatrizLectura";
import {
  colorLabel,
  fmtQtyMatriz,
  matrixViewFromSavedLineas,
  matrizToNumber,
  normLineaEnvio,
  orderUnitsReferenceFromPedido,
  tallaLabel,
} from "../Produccion/a3PedidoMatrizUtils";
import { lineasEnvioForPedido } from "./logisticaEnvioEditUtils";

const normStr = (v) => String(v ?? "").trim();
const CRITICAL_DATE_INPUTS = [
  { field: "InspectionDate", label: "Inspection date" },
  { field: "ExFD", label: "ExFD" },
  { field: "ETDF", label: "ETDF" },
  { field: "CutOff", label: "CUT-OFF" },
  { field: "ETD", label: "ETD" },
  { field: "DD", label: "DD" },
];

function getShipmentMethodMeta(method, theme) {
  const raw = String(method ?? "").trim();
  const normalized = raw.toLowerCase();
  const isSeaAir =
    normalized.includes("sea-air") ||
    normalized.includes("sea air") ||
    normalized.includes("sea/air") ||
    normalized.includes("air/sea") ||
    normalized.includes("air-sea");
  if (isSeaAir) {
    return {
      label: raw || "Sea-Air",
      color: "#14b8a6",
      icon: <AltRouteOutlinedIcon sx={{ fontSize: 18 }} />,
    };
  }
  if (normalized.includes("courier") || normalized.includes("mensaj")) {
    return {
      label: raw || "Courier",
      color: "#0f766e",
      icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />,
    };
  }
  if (
    normalized.includes("air") ||
    normalized.includes("aer") ||
    normalized.includes("flight")
  ) {
    return {
      label: raw || "Air",
      color: "#0ea5e9",
      icon: <FlightTakeoffOutlinedIcon sx={{ fontSize: 18 }} />,
    };
  }
  if (
    normalized.includes("sea") ||
    normalized.includes("mar") ||
    normalized.includes("boat")
  ) {
    return {
      label: raw || "Sea",
      color: "#2563eb",
      icon: <DirectionsBoatOutlinedIcon sx={{ fontSize: 18 }} />,
    };
  }
  if (
    normalized.includes("comb") ||
    normalized.includes("mix") ||
    normalized.includes("multi")
  ) {
    return {
      label: raw || "Combination",
      color: "#8b5cf6",
      icon: <AltRouteOutlinedIcon sx={{ fontSize: 18 }} />,
    };
  }
  return {
    label: raw || "Truck",
    color: "#ea580c",
    icon: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />,
  };
}

function pickArticuloCod(p, link) {
  const c = String(
    p?.CodArt ??
      p?.codArt ??
      p?.CODART ??
      p?.codart ??
      link?.CodArt ??
      link?.codArt ??
      "",
  ).trim();
  if (c) return c;
  return "";
}

function pickPedIds(p, link) {
  const pc = p?.IdPedc ?? p?.idPedc ?? link?.IdPedc ?? link?.idPedc;
  const pv = p?.IdPedv ?? p?.idPedv ?? link?.IdPedv ?? link?.idPedv;
  const idPedc = pc != null && pc !== "" ? Number(pc) : NaN;
  const idPedv = pv != null && pv !== "" ? Number(pv) : NaN;
  return {
    idPedc: Number.isFinite(idPedc) && idPedc > 0 ? idPedc : null,
    idPedv: Number.isFinite(idPedv) && idPedv > 0 ? idPedv : null,
  };
}

function editableRowsFromEnvioLineas(lineasRaw, { zeroUnits = false } = {}) {
  return (Array.isArray(lineasRaw) ? lineasRaw : []).map((l) => {
    const n = normLineaEnvio(l);
    return {
      CodFamTallaH: n.codFamTallaH,
      CodTallaH: n.codTallaH,
      CodTallaHDesc: n.codTallaHDesc,
      CodFamTallaV: n.codFamTallaV,
      CodTallaV: n.codTallaV,
      CodTallaVDesc: n.codTallaVDesc,
      Unidades: zeroUnits ? 0 : matrizToNumber(n.unidades),
    };
  });
}

function PedidoMatrizAccordion({
  link,
  fallbackDb,
  savedEnvioLineas = [],
  seedPedidoId = null,
  headerData = null,
  onHeaderChange = null,
  headerEditable = false,
  matrixEditable = false,
  onLineasChange = null,
  removable = false,
  removing = false,
  onRemove = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const p = link?.pedidoA3;
  const idPed = Number(p?.IdPresupuestoA3Pedido ?? link?.IdPresupuestoA3Pedido);
  const codArtBase = pickArticuloCod(p, link);
  const referencia = String(
    p?.Referencia ??
      p?.referencia ??
      link?.Referencia ??
      link?.referencia ??
      "",
  ).trim();
  const { idPedc, idPedv } = pickPedIds(p, link);
  const dbSource = String(
    p?.EmpresaA3 ?? p?.empresaA3 ?? fallbackDb ?? "STAGE_DESIGN",
  ).trim();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lineas, setLineas] = useState(null);
  const [lineasEditable, setLineasEditable] = useState([]);
  const [error, setError] = useState("");
  const [usingManualTemplate, setUsingManualTemplate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setUsingManualTemplate(false);
    try {
      let idPedcFinal = idPedc;
      let idPedvFinal = idPedv;
      let codArtFinal = codArtBase;
      let dbSourceFinal = dbSource || "STAGE_DESIGN";
      let artFamilyH = "";
      let artFamilyV = "";
      const dbSourcesToTry = new Set(
        [dbSourceFinal, fallbackDb, "STAGE_DESIGN", "ERNA_TRADE", "NML_SPAIN"]
          .map((x) => String(x || "").trim())
          .filter(Boolean),
      );
      const codArtCandidates = new Set(
        [codArtBase, referencia]
          .map((x) => String(x || "").trim())
          .filter(Boolean),
      );

      // Fallback: rehidrata el vínculo desde PresupuestoA3Pedidos (además dispara auto-sync A3).
      if ((!idPedcFinal && !idPedvFinal) || !codArtFinal) {
        const proyectoId = Number(p?.IdProyecto ?? link?.IdProyecto);
        const presupuestoId = Number(p?.IdPresupuesto ?? link?.IdPresupuesto);
        const idA3Pedido = Number(
          p?.IdPresupuestoA3Pedido ?? link?.IdPresupuestoA3Pedido,
        );
        let hit = null;
        if (
          Number.isFinite(proyectoId) &&
          proyectoId > 0 &&
          Number.isFinite(presupuestoId) &&
          presupuestoId > 0
        ) {
          const sync = await getPresupuestoA3Pedidos(
            proyectoId,
            presupuestoId,
          ).catch(() => ({ rows: [] }));
          const rowsSync = Array.isArray(sync?.rows) ? sync.rows : [];
          hit =
            rowsSync.find(
              (x) => Number(x?.IdPresupuestoA3Pedido) === idA3Pedido,
            ) || null;
        }
        if (!hit && Number.isFinite(idA3Pedido) && idA3Pedido > 0) {
          const all = await getAllPresupuestoA3Pedidos(2000).catch(() => ({
            rows: [],
          }));
          const rowsAll = Array.isArray(all?.rows) ? all.rows : [];
          hit =
            rowsAll.find(
              (x) => Number(x?.IdPresupuestoA3Pedido) === idA3Pedido,
            ) || null;
        }
        if (hit) {
          const pc = Number(hit?.IdPedc);
          const pv = Number(hit?.IdPedv);
          if (!idPedcFinal && Number.isFinite(pc) && pc > 0) idPedcFinal = pc;
          if (!idPedvFinal && Number.isFinite(pv) && pv > 0) idPedvFinal = pv;
          if (!codArtFinal) {
            codArtFinal = String(hit?.CodArt ?? "").trim();
          }
          const codHit = String(hit?.CodArt ?? "").trim();
          if (codHit) codArtCandidates.add(codHit);
          const dbHit = String(hit?.EmpresaA3 ?? "").trim();
          if (dbHit) {
            dbSourceFinal = dbHit;
            dbSourcesToTry.add(dbHit);
          }
        }
      }

      if (!idPedcFinal && !idPedvFinal) {
        setError(t("logisticaPedidos.envioMatrizRefSinA3"));
        setLineas([]);
        setUsingManualTemplate(false);
        return;
      }
      if (referencia) {
        const art = await searchA3Articulos(referencia, dbSourceFinal).catch(
          () => ({
            rows: [],
          }),
        );
        const rowsArt = Array.isArray(art?.rows) ? art.rows : [];
        const byRef = rowsArt.filter(
          (x) =>
            String(x?.REFERENCIA ?? x?.referencia ?? "").trim() === referencia,
        );
        byRef.forEach((x) => {
          const c = String(x?.CODART ?? x?.codart ?? "").trim();
          if (c) codArtCandidates.add(c);
        });
        rowsArt.slice(0, 5).forEach((x) => {
          const c = String(x?.CODART ?? x?.codart ?? "").trim();
          if (c) codArtCandidates.add(c);
        });
      }

      if (codArtFinal) codArtCandidates.add(codArtFinal);
      const codArtsToTry = Array.from(codArtCandidates);

      if (codArtFinal) {
        const artData = await searchA3Articulos(
          codArtFinal,
          dbSourceFinal,
        ).catch(() => ({ rows: [] }));
        const artRows = Array.isArray(artData?.rows) ? artData.rows : [];
        const artHit =
          artRows.find(
            (x) => normStr(x?.CODART ?? x?.codart) === normStr(codArtFinal),
          ) ||
          artRows[0] ||
          null;
        artFamilyH = normStr(
          artHit?.CODFAMTALLAH ?? artHit?.codfamtallah ?? "",
        );
        artFamilyV = normStr(
          artHit?.CODFAMTALLAV ?? artHit?.codfamtallav ?? "",
        );
      }

      let rows = [];
      let lastErr = "";
      for (const dbTry of dbSourcesToTry) {
        for (const codToTry of codArtsToTry.length ? codArtsToTry : [""]) {
          try {
            const data = await getA3PedidoLineasMatriz({
              idPedc: idPedcFinal ?? "",
              idPedv: idPedvFinal ?? "",
              codart: codToTry,
              dbSource: dbTry,
              fallbackByPedido: true,
            });
            const r = Array.isArray(data?.rows) ? data.rows : [];
            if (r.length) {
              rows = r;
              break;
            }
          } catch (e) {
            lastErr = e?.response?.data?.error || e?.message || "";
          }
        }
        if (rows.length) break;
      }
      if (!rows.length && codArtsToTry.length) {
        for (const dbTry of dbSourcesToTry) {
          try {
            const data = await getA3PedidoLineasMatriz({
              idPedc: idPedcFinal ?? "",
              idPedv: idPedvFinal ?? "",
              codart: "",
              dbSource: dbTry,
              fallbackByPedido: true,
            });
            const r = Array.isArray(data?.rows) ? data.rows : [];
            if (r.length) {
              rows = r;
              break;
            }
          } catch (e) {
            lastErr = e?.response?.data?.error || e?.message || lastErr;
          }
        }
      }
      // Si sigue vacío, rehidratar IdPedv/IdPedc/CodArt/EmpresaA3 desde CRM (a veces desactualizados) y reintentar.
      if (!rows.length && Number.isFinite(idPed) && idPed > 0) {
        const all = await getAllPresupuestoA3Pedidos(2500).catch(() => ({
          rows: [],
        }));
        const rowsAll = Array.isArray(all?.rows) ? all.rows : [];
        const hit =
          rowsAll.find((x) => Number(x?.IdPresupuestoA3Pedido) === idPed) ||
          null;
        if (hit) {
          const pc = Number(hit?.IdPedc);
          const pv = Number(hit?.IdPedv);
          if (Number.isFinite(pc) && pc > 0) idPedcFinal = pc;
          if (Number.isFinite(pv) && pv > 0) idPedvFinal = pv;
          const ca = String(hit?.CodArt ?? "").trim();
          if (ca) codArtCandidates.add(ca);
          const refHit = String(hit?.Referencia ?? "").trim();
          if (refHit) codArtCandidates.add(refHit);
          const dbHit = String(hit?.EmpresaA3 ?? "").trim();
          if (dbHit) {
            dbSourceFinal = dbHit;
            dbSourcesToTry.add(dbHit);
          }
          const codArtsRetry = Array.from(codArtCandidates);
          for (const dbTry of dbSourcesToTry) {
            for (const codToTry of codArtsRetry.length ? codArtsRetry : [""]) {
              try {
                const data = await getA3PedidoLineasMatriz({
                  idPedc: idPedcFinal ?? "",
                  idPedv: idPedvFinal ?? "",
                  codart: codToTry,
                  dbSource: dbTry,
                  fallbackByPedido: true,
                });
                const r = Array.isArray(data?.rows) ? data.rows : [];
                if (r.length) {
                  rows = r;
                  break;
                }
              } catch (e) {
                lastErr = e?.response?.data?.error || e?.message || lastErr;
              }
            }
            if (rows.length) break;
          }
          if (!rows.length && codArtsRetry.length) {
            for (const dbTry of dbSourcesToTry) {
              try {
                const data = await getA3PedidoLineasMatriz({
                  idPedc: idPedcFinal ?? "",
                  idPedv: idPedvFinal ?? "",
                  codart: "",
                  dbSource: dbTry,
                  fallbackByPedido: true,
                });
                const r = Array.isArray(data?.rows) ? data.rows : [];
                if (r.length) {
                  rows = r;
                  break;
                }
              } catch (e) {
                lastErr = e?.response?.data?.error || e?.message || lastErr;
              }
            }
          }
        }
      }
      const mapped = a3MatrizRowsToLineasEnvio(rows);
      if (!mapped.length && lastErr) {
        setError(lastErr);
      }

      const keyOf = (l) =>
        [
          String(l?.CodFamTallaH ?? l?.codFamTallaH ?? "")
            .trim()
            .toUpperCase(),
          String(l?.CodTallaH ?? l?.codTallaH ?? "")
            .trim()
            .toUpperCase(),
          String(l?.CodFamTallaV ?? l?.codFamTallaV ?? "")
            .trim()
            .toUpperCase(),
          String(l?.CodTallaV ?? l?.codTallaV ?? "")
            .trim()
            .toUpperCase(),
        ].join("|");

      const savedForPedido = editableRowsFromEnvioLineas(
        lineasEnvioForPedido(savedEnvioLineas, idPed),
      );
      const savedEditableZero = editableRowsFromEnvioLineas(
        lineasEnvioForPedido(savedEnvioLineas, idPed),
        { zeroUnits: true },
      );
      const savedByKey = new Map(
        savedForPedido.map((l) => [keyOf(l), matrizToNumber(l?.Unidades)]),
      );

      setLineas(mapped);

      if (mapped.length) {
        // Solo la matriz persistida de ESTE pedido. La consolidada del envío
        // no se pinta aquí: si no hay filas propias, se usa A3.
        setUsingManualTemplate(false);
        setLineasEditable(
          mapped.map((l) => ({
            ...l,
            Unidades: savedByKey.has(keyOf(l))
              ? matrizToNumber(savedByKey.get(keyOf(l)))
              : matrizToNumber(l?.Unidades),
          })),
        );
        return;
      }

      if (savedEditableZero.length) {
        // Si A3 no trae desglose, al menos permitimos repartir manualmente
        // usando la estructura de la matriz consolidada actual del envío,
        // filtrada a las familias del artículo si se conocen.
        if (!(artFamilyH && artFamilyV)) {
          setUsingManualTemplate(false);
          setLineasEditable([]);
          setError(t("logisticaPedidos.envioMatrizManualNoFamilyHint"));
          return;
        }
        const filteredTemplate = savedEditableZero.filter(
          (l) =>
            normStr(l?.CodFamTallaH) === artFamilyH &&
            normStr(l?.CodFamTallaV) === artFamilyV,
        );
        if (!filteredTemplate.length) {
          setUsingManualTemplate(false);
          setLineasEditable([]);
          setError(t("logisticaPedidos.envioMatrizManualNoFamilyHint"));
          return;
        }
        setUsingManualTemplate(true);
        setLineasEditable(filteredTemplate);
        return;
      }

      setUsingManualTemplate(false);
      setLineasEditable([]);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setLineas([]);
      setLineasEditable([]);
      setUsingManualTemplate(false);
    } finally {
      setLoading(false);
    }
  }, [
    idPedc,
    idPedv,
    codArtBase,
    referencia,
    dbSource,
    fallbackDb,
    p,
    link,
    t,
    idPed,
    savedEnvioLineas,
  ]);

  useEffect(() => {
    if (matrixEditable && lineas === null && !loading) {
      void load();
    }
  }, [matrixEditable, lineas, loading, load]);

  useEffect(() => {
    if (!matrixEditable || !onLineasChange) return;
    if (!Array.isArray(lineasEditable)) return;
    onLineasChange(idPed, lineasEditable);
  }, [matrixEditable, onLineasChange, idPed, lineasEditable]);

  const onExpand = (_, isEx) => {
    setExpanded(isEx);
    if (isEx && lineas === null && !loading) void load();
  };

  const title = (() => {
    const base = referencia
      ? `#${idPed} · ${referencia} · ${codArtBase || "—"}`
      : `#${idPed} · ${codArtBase || "—"}`;
    const u = orderUnitsReferenceFromPedido(p);
    return u > 0 ? `${base} · ${fmtQtyMatriz(u)} uds (ref.)` : base;
  })();
  const shipmentMethodMeta = getShipmentMethodMeta(
    headerData?.ShipmentMethod,
    theme,
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={onExpand}
      variant="outlined"
      disableGutters
      sx={{
        mb: 1,
        borderRadius: 2.2,
        overflow: "hidden",
        borderColor: alpha(shipmentMethodMeta.color, 0.16),
        backgroundColor: alpha(theme.palette.background.paper, 0.96),
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 24px rgba(0,0,0,0.18)"
            : "0 8px 24px rgba(15,23,42,0.04)",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.5,
          py: 0.6,
          background: `linear-gradient(135deg, ${alpha(shipmentMethodMeta.color, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          "& .MuiAccordionSummary-content": {
            my: 0,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1.05}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: shipmentMethodMeta.color,
                backgroundColor: alpha(shipmentMethodMeta.color, 0.12),
                flexShrink: 0,
              }}
            >
              {shipmentMethodMeta.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={800}
                sx={{ lineHeight: 1.2 }}
              >
                {title}
              </Typography>
              <Stack
                direction="row"
                gap={0.6}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.45 }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={shipmentMethodMeta.label}
                  sx={{
                    borderRadius: 999,
                    borderColor: alpha(shipmentMethodMeta.color, 0.22),
                    color: shipmentMethodMeta.color,
                  }}
                />
                {headerData?.NCartons != null && headerData?.NCartons !== "" ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Cartons ${headerData.NCartons}`}
                    sx={{ borderRadius: 999 }}
                  />
                ) : null}
                {headerData?.ETA ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`ETA ${String(headerData.ETA).slice(0, 10)}`}
                    sx={{ borderRadius: 999 }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Stack>
          {removable && typeof onRemove === "function" ? (
            <Tooltip title="Quitar pedido de este envío">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={removing}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(idPed);
                  }}
                >
                  {removing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <LinkOffIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 1.5,
          py: 1.35,
          backgroundColor: alpha(theme.palette.background.paper, 0.84),
        }}
      >
        {headerData ? (
          <Box sx={{ mb: 1.2 }}>
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="center"
              sx={{ mb: 0.85 }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: shipmentMethodMeta.color,
                  backgroundColor: alpha(shipmentMethodMeta.color, 0.12),
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 0.45,
                }}
              >
                Cabecera logística del pedido
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 0.9,
                p: 1.1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(shipmentMethodMeta.color, 0.12),
                backgroundColor: alpha(theme.palette.background.default, 0.4),
              }}
            >
              <TextField
                label="Shipment"
                size="small"
                value={headerData.ShipmentMethod ?? ""}
                disabled={!headerEditable}
                onChange={(e) =>
                  onHeaderChange?.(idPed, "ShipmentMethod", e.target.value)
                }
              />
              <TextField
                label="Cartons"
                size="small"
                type="number"
                value={headerData.NCartons ?? ""}
                disabled={!headerEditable}
                onChange={(e) =>
                  onHeaderChange?.(idPed, "NCartons", e.target.value)
                }
              />
              <TextField
                label="Gross kg"
                size="small"
                value={headerData.GrossWeightKgs ?? ""}
                disabled={!headerEditable}
                onChange={(e) =>
                  onHeaderChange?.(idPed, "GrossWeightKgs", e.target.value)
                }
              />
              <TextField
                label="CBM"
                size="small"
                value={headerData.TotalVolumeCbm ?? ""}
                disabled={!headerEditable}
                onChange={(e) =>
                  onHeaderChange?.(idPed, "TotalVolumeCbm", e.target.value)
                }
              />
              <TextField
                label="ETA"
                type="date"
                size="small"
                value={headerData.ETA ?? ""}
                disabled={!headerEditable}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => onHeaderChange?.(idPed, "ETA", e.target.value)}
              />
              <TextField
                label="Booking"
                type="date"
                size="small"
                value={headerData.BookingDone ?? ""}
                disabled={!headerEditable}
                InputLabelProps={{ shrink: true }}
                onChange={(e) =>
                  onHeaderChange?.(idPed, "BookingDone", e.target.value)
                }
              />
              {CRITICAL_DATE_INPUTS.map((item) => (
                <TextField
                  key={`${idPed}-${item.field}`}
                  label={item.label}
                  type="date"
                  size="small"
                  value={headerData?.[item.field] ?? ""}
                  disabled={!headerEditable}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) =>
                    onHeaderChange?.(idPed, item.field, e.target.value)
                  }
                />
              ))}
              <TextField
                label="Docs approved"
                type="date"
                size="small"
                value={headerData.DocumentsApproved ?? ""}
                disabled={!headerEditable}
                InputLabelProps={{ shrink: true }}
                onChange={(e) =>
                  onHeaderChange?.(idPed, "DocumentsApproved", e.target.value)
                }
              />
              <TextField
                label="Telex/docs sent"
                type="date"
                size="small"
                value={headerData.OriginalTelexDocsSemt ?? ""}
                disabled={!headerEditable}
                InputLabelProps={{ shrink: true }}
                onChange={(e) =>
                  onHeaderChange?.(
                    idPed,
                    "OriginalTelexDocsSemt",
                    e.target.value,
                  )
                }
              />
            </Box>
          </Box>
        ) : null}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : matrixEditable && lineasEditable.length ? (
          (() => {
            const mv = matrixViewFromSavedLineas(lineasEditable);
            if (!mv)
              return (
                <Box>
                  {usingManualTemplate ? (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      {t("logisticaPedidos.envioMatrizManualHint")}
                    </Alert>
                  ) : null}
                  <EnvioMatrizLectura lineas={lineasEditable} />
                </Box>
              );
            const { famH, famV, tallasH, tallasV, qtyAt, colTotals, total } =
              mv;
            return (
              <Box>
                {usingManualTemplate ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {t("logisticaPedidos.envioMatrizManualHint")}
                  </Alert>
                ) : null}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={0.9}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  sx={{ mb: 0.9 }}
                >
                  <Stack direction="row" gap={0.7} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Cols ${famH}`}
                      sx={{ borderRadius: 999, fontWeight: 700 }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Filas ${famV}`}
                      sx={{ borderRadius: 999, fontWeight: 700 }}
                    />
                  </Stack>
                  <Chip
                    size="small"
                    label={`Total matriz ${fmtQtyMatriz(total)} uds`}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      backgroundColor: alpha(shipmentMethodMeta.color, 0.12),
                      color: shipmentMethodMeta.color,
                    }}
                  />
                </Stack>
                <Box
                  sx={{
                    overflowX: "auto",
                    maxWidth: "100%",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: alpha(shipmentMethodMeta.color, 0.12),
                    backgroundColor: alpha(
                      theme.palette.background.paper,
                      0.92,
                    ),
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      minWidth: 380,
                      "& .MuiTableCell-root": {
                        borderBottomColor: alpha(theme.palette.divider, 0.6),
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{
                            position: "sticky",
                            left: 0,
                            bgcolor: alpha(
                              theme.palette.background.paper,
                              0.98,
                            ),
                            zIndex: 2,
                            fontWeight: 800,
                            minWidth: 140,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: 0.3,
                          }}
                        >
                          Color / Talla
                        </TableCell>
                        {tallasH.map((h) => (
                          <TableCell
                            key={`eh-${String(h.CODTALLA)}`}
                            align="center"
                            sx={{
                              backgroundColor: alpha(
                                shipmentMethodMeta.color,
                                0.06,
                              ),
                              minWidth: 72,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 800,
                                color: "text.primary",
                                letterSpacing: 0.2,
                              }}
                            >
                              {tallaLabel(h)}
                            </Typography>
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 800,
                            minWidth: 92,
                            backgroundColor: alpha(
                              shipmentMethodMeta.color,
                              0.08,
                            ),
                          }}
                        >
                          Σ fila (uds)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tallasV.map((v, rowIndex) => {
                        const codV = String(v.CODTALLA);
                        const rowSum = tallasH.reduce(
                          (s, h) => s + qtyAt(codV, String(h.CODTALLA)),
                          0,
                        );
                        return (
                          <TableRow
                            key={`ev-${codV}`}
                            sx={{
                              backgroundColor:
                                rowIndex % 2 === 0
                                  ? alpha(
                                      theme.palette.background.default,
                                      0.26,
                                    )
                                  : "transparent",
                              "&:hover": {
                                backgroundColor: alpha(
                                  shipmentMethodMeta.color,
                                  0.06,
                                ),
                              },
                            }}
                          >
                            <TableCell
                              align="left"
                              sx={{
                                position: "sticky",
                                left: 0,
                                bgcolor:
                                  rowIndex % 2 === 0
                                    ? alpha(
                                        theme.palette.background.paper,
                                        0.98,
                                      )
                                    : alpha(
                                        theme.palette.background.paper,
                                        0.94,
                                      ),
                                zIndex: 1,
                                whiteSpace: "nowrap",
                                fontWeight: 700,
                                textAlign: "left",
                                minWidth: 140,
                              }}
                            >
                              {colorLabel(v)}
                            </TableCell>
                            {tallasH.map((h) => {
                              const codH = String(h.CODTALLA);
                              const idx = lineasEditable.findIndex(
                                (x) =>
                                  String(x?.CodTallaH ?? "").trim() === codH &&
                                  String(x?.CodTallaV ?? "").trim() === codV,
                              );
                              const q = qtyAt(codV, codH);
                              return (
                                <TableCell
                                  key={`ec-${codV}-${codH}`}
                                  align="center"
                                  sx={{
                                    py: 0.45,
                                    minWidth: 72,
                                    backgroundColor:
                                      q > 0
                                        ? alpha(theme.palette.info.main, 0.06)
                                        : alpha(
                                            theme.palette.background.default,
                                            0.12,
                                          ),
                                  }}
                                >
                                  {idx < 0 ? (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      —
                                    </Typography>
                                  ) : (
                                    <TextField
                                      size="small"
                                      value={String(q > 0 ? q : 0)}
                                      sx={{
                                        width: 62,
                                        "& .MuiOutlinedInput-root": {
                                          backgroundColor:
                                            q > 0
                                              ? alpha(
                                                  theme.palette.info.main,
                                                  0.16,
                                                )
                                              : alpha(
                                                  theme.palette.background
                                                    .default,
                                                  0.24,
                                                ),
                                          borderRadius: 1.3,
                                          opacity: q > 0 ? 1 : 0.72,
                                          boxShadow:
                                            q > 0
                                              ? `0 4px 10px ${alpha(
                                                  theme.palette.info.main,
                                                  0.14,
                                                )}`
                                              : "none",
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: alpha(
                                            q > 0
                                              ? theme.palette.info.main
                                              : theme.palette.text.disabled,
                                            q > 0 ? 0.26 : 0.1,
                                          ),
                                        },
                                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor: alpha(
                                              q > 0
                                                ? theme.palette.info.main
                                                : theme.palette.text.secondary,
                                              q > 0 ? 0.4 : 0.18,
                                            ),
                                          },
                                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                          {
                                            borderColor:
                                              q > 0
                                                ? theme.palette.info.main
                                                : theme.palette.text.secondary,
                                            borderWidth: 1,
                                          },
                                        "& .MuiInputBase-input": {
                                          fontSize: 12.5,
                                          py: 0.38,
                                          px: 0.5,
                                          fontWeight: q > 0 ? 900 : 700,
                                          color:
                                            q > 0
                                              ? theme.palette.info.dark
                                              : theme.palette.text.disabled,
                                        },
                                      }}
                                      inputProps={{
                                        inputMode: "decimal",
                                        style: { textAlign: "center" },
                                      }}
                                      onChange={(e) => {
                                        const raw = String(
                                          e.target.value ?? "",
                                        ).replace(",", ".");
                                        const n = Number(raw);
                                        const unidades =
                                          Number.isFinite(n) && n >= 0 ? n : 0;
                                        setLineasEditable((prev) =>
                                          prev.map((x, i) =>
                                            i === idx
                                              ? {
                                                  ...x,
                                                  Unidades:
                                                    matrizToNumber(unidades),
                                                }
                                              : x,
                                          ),
                                        );
                                      }}
                                    />
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 800,
                                backgroundColor: alpha(
                                  shipmentMethodMeta.color,
                                  0.08,
                                ),
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: 34,
                                  px: 0.8,
                                  py: 0.28,
                                  borderRadius: 999,
                                  fontWeight: 900,
                                  color:
                                    rowSum > 0
                                      ? shipmentMethodMeta.color
                                      : theme.palette.text.secondary,
                                  backgroundColor:
                                    rowSum > 0
                                      ? alpha(shipmentMethodMeta.color, 0.14)
                                      : alpha(
                                          theme.palette.background.paper,
                                          0.8,
                                        ),
                                }}
                              >
                                {fmtQtyMatriz(rowSum)}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow
                        sx={{
                          backgroundColor: alpha(shipmentMethodMeta.color, 0.1),
                        }}
                      >
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 800,
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            bgcolor: alpha(
                              theme.palette.background.paper,
                              0.98,
                            ),
                          }}
                        >
                          Σ columna (uds)
                        </TableCell>
                        {colTotals.map((ct, i) => (
                          <TableCell
                            key={`ect-${i}`}
                            align="center"
                            sx={{ fontWeight: 800 }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 34,
                                px: 0.8,
                                py: 0.28,
                                borderRadius: 999,
                                fontWeight: 900,
                                color:
                                  ct > 0
                                    ? shipmentMethodMeta.color
                                    : theme.palette.text.secondary,
                                backgroundColor:
                                  ct > 0
                                    ? alpha(shipmentMethodMeta.color, 0.14)
                                    : alpha(
                                        theme.palette.background.paper,
                                        0.82,
                                      ),
                              }}
                            >
                              {fmtQtyMatriz(ct)}
                            </Box>
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 900,
                            color: shipmentMethodMeta.color,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: 40,
                              px: 0.95,
                              py: 0.34,
                              borderRadius: 999,
                              fontWeight: 900,
                              color: shipmentMethodMeta.color,
                              backgroundColor: alpha(
                                shipmentMethodMeta.color,
                                0.18,
                              ),
                              boxShadow: `0 4px 12px ${alpha(
                                shipmentMethodMeta.color,
                                0.12,
                              )}`,
                            }}
                          >
                            {fmtQtyMatriz(total)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            );
          })()
        ) : lineas && lineas.length ? (
          <EnvioMatrizLectura lineas={lineas} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t("logisticaPedidos.envioMatrizRefVacio")}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Varias matrices apiladas (acordeón), una por pedido vinculado — referencia lectura desde A3.
 * Recomendado cuando hay más de un producto/pedido en el mismo envío.
 */
export default function LogisticaPedidosMatricesReferencia({
  pedidoLinks,
  fallbackDb = "STAGE_DESIGN",
  envioLineas = [],
  seedPedidoId = null,
  showEnvioConsolidada = true,
  pedidoHeaders = {},
  onHeaderChange = null,
  headerEditable = false,
  matrixEditable = false,
  onPedidoLineasChange = null,
  removablePedidos = false,
  removingPedidoId = null,
  onRemovePedido = null,
}) {
  const { t } = useTranslation();
  const links = Array.isArray(pedidoLinks) ? pedidoLinks : [];
  if (links.length < 2) return null;

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {t("logisticaPedidos.envioMatricesPedidosTitulo")}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        {t("logisticaPedidos.envioMatricesPedidosHint")}
      </Typography>
      {showEnvioConsolidada &&
      Array.isArray(envioLineas) &&
      envioLineas.length > 0 ? (
        <Box sx={{ mb: 1.2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.6 }}
          >
            Matriz consolidada guardada del envío
          </Typography>
          <EnvioMatrizLectura lineas={envioLineas} />
        </Box>
      ) : null}
      <Stack spacing={0.75}>
        {links.map((lnk) => (
          <PedidoMatrizAccordion
            key={lnk?.Id ?? lnk?.pedidoA3?.IdPresupuestoA3Pedido}
            link={lnk}
            fallbackDb={fallbackDb}
            savedEnvioLineas={envioLineas}
            seedPedidoId={seedPedidoId}
            headerData={
              pedidoHeaders?.[
                Number(
                  lnk?.pedidoA3?.IdPresupuestoA3Pedido ??
                    lnk?.IdPresupuestoA3Pedido,
                )
              ] ?? null
            }
            onHeaderChange={onHeaderChange}
            headerEditable={headerEditable}
            matrixEditable={matrixEditable}
            onLineasChange={onPedidoLineasChange}
            removable={removablePedidos}
            removing={
              Number(removingPedidoId) ===
              Number(
                lnk?.pedidoA3?.IdPresupuestoA3Pedido ??
                  lnk?.IdPresupuestoA3Pedido,
              )
            }
            onRemove={onRemovePedido}
          />
        ))}
      </Stack>
    </Box>
  );
}
